import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { DataContext } from ".";
import InfoIcon from "@mui/icons-material/Info";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";
import { useTradingColors } from "./tradingColorConfig";
import "./Spot.css";
import OrderBookTooltip, { formatOrderBookNumber } from "../../Common/OrderBookTooltip";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  USDT: "$",
  AED: "د.إ",
  EUR: "€",
  SGD: "S$",
};

const title = "Orderbook";
const description =
  "Sell offers are displayed on top in red color, and Buy offers are displayed at the bottom in green color. The right side of the order book is displaying the current 50 trades that took place in the market in the last 24 hours.";

const OrderBook = () => {
  const {
    chartData,
    setFinalBiddata,
    setFinalAskdata,
    currencycode,
    basecurrency,
    lowPrice,
    highPrice,
    setSellData,
    setBuyData,
    setLastAskItem,
    setFirstBidItem,
    lastAskItem,
    firstBidItem,
  } = useContext(DataContext);

  const tableContentRef = useRef(null);
  const websocketRef = useRef({ socket: null, subscribeMessage: null });
  const theme = localStorage.getItem("theme");
  const { brokerBasicInfo } = useSelector((state) => state);
  const { upColor, downColor } = useTradingColors();

  const [biddata, setBiddata] = useState([]);
  const [askdata, setAskdata] = useState([]);
  const [tradep, setTradep] = useState(6);
  const [token, setToken] = useState(null);
  const [show, setShow] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [bookViewMode, setBookViewMode] = useState("default"); // "default" | "bids" | "asks"

  const handleRowMouseEnter = (type, index, targetEl) => {
    updateHover(type, index, targetEl);
  };

  const handleRowMouseMove = (type, index, targetEl) => {
    if (!hoverInfo || hoverInfo.type !== type || hoverInfo.index !== index) {
      updateHover(type, index, targetEl);
    }
  };

  const updateHover = (type, index, targetEl) => {
    if (!targetEl) return;
    const rect = targetEl.getBoundingClientRect();
    const prc = parseInt(hasValidP(localStorage.getItem("priceprc")) ? localStorage.getItem("priceprc") : 6);
    const amtPrc = parseInt(hasValidP(localStorage.getItem("amountprc")) ? localStorage.getItem("amountprc") : 6);

    let sumBase = 0;
    let sumQuote = 0;

    if (type === "ask") {
      for (let i = index; i < asksWithDepth.length; i++) {
        const item = asksWithDepth[i];
        const p = parseFloat(item.price) || 0;
        const a = parseFloat(item.amount) || 0;
        sumBase += a;
        sumQuote += p * a;
      }
    } else {
      for (let i = 0; i <= index && i < bidsWithDepth.length; i++) {
        const item = bidsWithDepth[i];
        const p = parseFloat(item.price) || 0;
        const a = parseFloat(item.amount) || 0;
        sumBase += a;
        sumQuote += p * a;
      }
    }

    const avgPrice = sumBase > 0 ? sumQuote / sumBase : 0;

    setHoverInfo({
      type,
      index,
      rect,
      avgPrice: formatOrderBookNumber(avgPrice, prc),
      sumBase: formatOrderBookNumber(sumBase, amtPrc),
      sumQuote: formatOrderBookNumber(sumQuote, 6),
    });
  };

  const textColor = "#f1b900";
  const containerClass = "sp-highlow";

  const webSocketEndPoint = "https://stream.paybito.com/SocketStream/ws";
  const topic = "/user/topic/stream";
  const topicForBtcUsd = "/topic/stream/BTCUSD";

  let stompClient = null;
  let reqNo = null;

  const handleClose = () => setShow(false);

  // ------------------- UTILITY FUNCTIONS -------------------


  const hasValidP = (value) => {
    // null / undefined / false
    if (value === null || value === undefined || value === false) return false;

    const str = String(value).trim().toLowerCase();

    // blank / "undefined" / "null"
    return str !== "" && str !== "undefined" && str !== "null";
  };




  const getPrice = (m) => {
    const prc = localStorage.getItem("priceprc");
    const p = parseInt(hasValidP(prc) ? prc : 6);
    return parseFloat(m.price).toFixed(p);
  };

  const getAmount = (m) => {
    const prc = localStorage.getItem("amountprc");
    const s = hasValidP(prc) ? prc : 6;
    return parseFloat(m.amount).toFixed(s);
  };

  // Clicking an ASK (sell-side) price sets the BUY panel's price,
  // and clicking a BID (buy-side) price sets the SELL panel's price.
  const handleSellClick = (data) => setBuyData(data);
  const handleBuyClick = (data) => setSellData(data);

  const formatNumber = (num) => {
    const strNum = num.toString();
    const [integerPart, decimalPart = ""] = strNum.split(".");
    return decimalPart.length < 6
      ? `${integerPart}.${decimalPart.padEnd(6, "0")}`
      : strNum;
  };

  // ------------------- WEBSOCKET FUNCTIONS -------------------

  const connectWebSocket = () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return;

    const ws = new SockJS(webSocketEndPoint);
    stompClient = Stomp.over(ws);

    stompClient.connect(
      {},
      () => {
        websocketRef.current.socket = stompClient;

        let topicToSubscribe =
          currencycode?.toLowerCase() === "btc" &&
            basecurrency?.toLowerCase() === "usd"
            ? topicForBtcUsd
            : topic;

        stompClient.subscribe(topicToSubscribe, (sdkEvent) => {
          const message = JSON.parse(sdkEvent.body);
          websocketRef.current.subscribeMessage?.(message);
          onMessageReceived(sdkEvent);
        });

        subscribe();
      },
      errorCallback
    );
  };

  const errorCallback = (error) => {
    console.error("WebSocket Error:", error);
    if (localStorage.getItem("access_token")) {
      setTimeout(connectWebSocket, 5000);
    }
  };

  const sendMessage = (message) => {
    if (!stompClient) return;

    let sendEndpoint =
      currencycode === "btc" && basecurrency === "usd"
        ? "/app/sendRequest/BTCUSD"
        : "/app/sendRequest";

    stompClient.send(sendEndpoint, {}, JSON.stringify(message));
  };

  const onMessageReceived = (message) => {
    const parsedMessage = JSON.parse(message.body);
    // Optional: handle parsedMessage if needed
  };

  const subscribe = () => {
    if (!currencycode || !basecurrency) return;
    reqNo = Math.floor(Math.random() * 1000) + 1;
    const req = {
      method: "SUBSCRIBE",
      params: [
        `${currencycode}/${basecurrency}@ticker`,
        `${currencycode}${basecurrency}@depth`,
        `${currencycode}${basecurrency}@trade`,
      ],
      id: reqNo,
    };
    setTimeout(() => sendMessage(req), 10000);
    localStorage.setItem("isUnsubscribeOccured", "false");
  };

  const disconnectWebSocket = () => {
    if (stompClient?.connected) {
      stompClient.disconnect(() => {
        websocketRef.current.socket = null;
        console.log("WebSocket Disconnected");
      });
    }
  };

  // ------------------- API FUNCTIONS -------------------

  const serverSentEventForOrderbookAsk = async () => {
    try {
      const response = await axios.get(
        `https://stream.paybito.com/SocketStream/api/depth?symbol=${currencycode}${basecurrency}&limit=150`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      const result = response.data;
      const askSideData = [...result.ask].sort((a, b) => b.price - a.price);
      const bidSideData = [...result.bid];
      setAskdata(askSideData);
      setBiddata(bidSideData);

      websocketRef.current.subscribeMessage = (orderbookData) => {
        if (orderbookData.e === "depthUpdate") {
          const prc = localStorage.getItem("priceprc");
          const currentTradeP = parseInt(hasValidP(prc) ? prc : 6);

          // ASK UPDATE
          if (orderbookData.ask) {
            setAskdata((prev) => {
              const updated = [...prev];
              orderbookData.ask.forEach((item) => {
                const priceFixed = parseFloat(item.price).toFixed(currentTradeP);
                const index = updated.findIndex(
                  (x) => parseFloat(x.price).toFixed(currentTradeP) === priceFixed
                );
                if (item.action === "NEW") {
                  updated.push({
                    price: priceFixed,
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  });
                } else if (item.action === "CHANGE" && index > -1) {
                  updated[index] = {
                    ...updated[index],
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  };
                } else if (
                  (item.action === "DELETE" || item.action === "TRADEDELETE") &&
                  index > -1
                ) {
                  updated.splice(index, 1);
                }
              });
              return updated.sort((a, b) => b.price - a.price).slice(-150);
            });
          }

          // BID UPDATE
          if (orderbookData.bid) {
            setBiddata((prev) => {
              const updated = [...prev];
              orderbookData.bid.forEach((item) => {
                const priceFixed = parseFloat(item.price).toFixed(currentTradeP);
                const index = updated.findIndex(
                  (x) => parseFloat(x.price).toFixed(currentTradeP) === priceFixed
                );
                if (item.action === "NEW") {
                  updated.push({
                    price: priceFixed,
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  });
                } else if (item.action === "CHANGE" && index > -1) {
                  updated[index] = {
                    ...updated[index],
                    amount: parseFloat(item.quantity).toFixed(currentTradeP),
                    newly: true,
                  };
                } else if (
                  (item.action === "DELETE" || item.action === "TRADEDELETE") &&
                  index > -1
                ) {
                  updated.splice(index, 1);
                }
              });
              return updated.sort((a, b) => b.price - a.price).slice(0, 150);
            });
          }
        }
      };

      connectWebSocket();
    } catch (error) {
      console.error("Error fetching order book data:", error);
    }
  };

  // ------------------- EFFECTS -------------------

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setToken(token);
    serverSentEventForOrderbookAsk();

    return () => disconnectWebSocket();
  }, [currencycode, basecurrency]);

  useEffect(() => {
    setFinalBiddata(biddata);
    setFinalAskdata(askdata);
  }, [biddata, askdata, setFinalBiddata, setFinalAskdata]);

  // ── Clear the "newly" highlight flag after 800ms so the flash plays once ──
  useEffect(() => {
    const hasNewlyAsk = askdata.some((d) => d.newly);
    const hasNewlyBid = biddata.some((d) => d.newly);

    if (!hasNewlyAsk && !hasNewlyBid) return;

    const timer = setTimeout(() => {
      if (hasNewlyAsk) {
        setAskdata((prev) =>
          prev.map((d) => (d.newly ? { ...d, newly: false } : d))
        );
      }
      if (hasNewlyBid) {
        setBiddata((prev) =>
          prev.map((d) => (d.newly ? { ...d, newly: false } : d))
        );
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [askdata, biddata]);

  useEffect(() => {
    if (askdata && askdata.length > 0) {
      const sortedAsk = [...askdata].sort((a, b) => b.price - a.price);
      setLastAskItem(sortedAsk[sortedAsk.length - 1]);
    }
  }, [askdata]);

  useEffect(() => {
    if (biddata && biddata.length > 0) {
      const sortedBid = [...biddata].sort((a, b) => b.price - a.price);
      setFirstBidItem(sortedBid[0]);
    }
  }, [biddata]);

  useEffect(() => {
    if (lastAskItem) {
      console.log("last item of askdata after sorting", lastAskItem);
    }
  }, [lastAskItem]);

  useEffect(() => {
    if (firstBidItem) {
      console.log("first item of biddata after sorting", firstBidItem);
    }
  }, [firstBidItem]);

  // ── Calculate Binance-style Relative Order Size Overlays ──
  const asksWithDepth = React.useMemo(() => {
    if (!askdata || !askdata.length) return [];

    const sortedAsks = [...askdata].sort(
      (a, b) => parseFloat(b.price) - parseFloat(a.price)
    );

    let maxAmount = 0;
    sortedAsks.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      if (amt > maxAmount) maxAmount = amt;
    });
    if (maxAmount <= 0) maxAmount = 1;

    return sortedAsks.map((item) => {
      const amt = parseFloat(item.amount) || 0;
      const percent = Math.min(100, Math.max(2, (amt / maxAmount) * 100));
      return {
        ...item,
        depthPercent: percent.toFixed(1),
      };
    });
  }, [askdata]);

  // ── Calculate Binance-style Relative Order Size Overlays for Bids ──
  const bidsWithDepth = React.useMemo(() => {
    if (!biddata || !biddata.length) return [];

    const sortedBids = [...biddata].sort(
      (a, b) => parseFloat(b.price) - parseFloat(a.price)
    );

    let maxAmount = 0;
    sortedBids.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      if (amt > maxAmount) maxAmount = amt;
    });
    if (maxAmount <= 0) maxAmount = 1;

    return sortedBids.map((item) => {
      const amt = parseFloat(item.amount) || 0;
      const percent = Math.min(100, Math.max(2, (amt / maxAmount) * 100));
      return {
        ...item,
        depthPercent: percent.toFixed(1),
      };
    });
  }, [biddata]);

  // ── Calculate Binance-style Order Book Buy/Sell Ratio ──
  const { buyPercent, sellPercent, buyBarWidth, sellBarWidth } = React.useMemo(() => {
    if (!biddata?.length && !askdata?.length) {
      return { buyPercent: "50.00", sellPercent: "50.00", buyBarWidth: 50, sellBarWidth: 50 };
    }

    let totalBid = 0;
    for (let i = 0; i < biddata.length; i++) {
      totalBid += parseFloat(biddata[i]?.amount) || 0;
    }

    let totalAsk = 0;
    for (let i = 0; i < askdata.length; i++) {
      totalAsk += parseFloat(askdata[i]?.amount) || 0;
    }

    const total = totalBid + totalAsk;
    if (total <= 0) {
      return { buyPercent: "50.00", sellPercent: "50.00", buyBarWidth: 50, sellBarWidth: 50 };
    }

    const buyRatio = (totalBid / total) * 100;
    const sellRatio = (totalAsk / total) * 100;

    return {
      buyPercent: buyRatio.toFixed(2),
      sellPercent: sellRatio.toFixed(2),
      buyBarWidth: Math.min(100, Math.max(0, buyRatio)),
      sellBarWidth: Math.min(100, Math.max(0, sellRatio)),
    };
  }, [biddata, askdata]);

  useEffect(() => {
    if (tableContentRef.current) {
      tableContentRef.current.scrollTop = tableContentRef.current.scrollHeight;
      const t = setTimeout(() => {
        if (tableContentRef.current) {
          tableContentRef.current.scrollTop = tableContentRef.current.scrollHeight;
        }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [asksWithDepth]);

  // ------------------- RENDER -------------------

  return (
    <div style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ── ORDERBOOK TOOLBAR (Binance 3 View Modes & Tick Size Selector) ── */}
      <div
        className="orderbook-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "3px 8px",
          height: "26px",
          minHeight: "26px",
          backgroundColor: "var(--body-color-less)",
          borderBottom: theme === "light" ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        {/* 3 View Mode Switchers */}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {/* Default Mode (Asks top, Bids bottom) */}
          <button
            type="button"
            onClick={() => setBookViewMode("default")}
            title="Default (Order Book)"
            style={{
              background: bookViewMode === "default" ? (theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)") : "transparent",
              border: "none",
              padding: "2px 4px",
              borderRadius: "3px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="2" rx="0.5" fill={downColor} />
              <rect x="2" y="5" width="12" height="2" rx="0.5" fill={downColor} />
              <rect x="2" y="9" width="12" height="2" rx="0.5" fill={upColor} />
              <rect x="2" y="12" width="12" height="2" rx="0.5" fill={upColor} />
            </svg>
          </button>

          {/* Buy Only Mode (Bids) */}
          <button
            type="button"
            onClick={() => setBookViewMode("bids")}
            title="Buy Orders Only"
            style={{
              background: bookViewMode === "bids" ? (theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)") : "transparent",
              border: "none",
              padding: "2px 4px",
              borderRadius: "3px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="2" rx="0.5" fill={upColor} />
              <rect x="2" y="5" width="12" height="2" rx="0.5" fill={upColor} />
              <rect x="2" y="8" width="12" height="2" rx="0.5" fill={upColor} />
              <rect x="2" y="11" width="12" height="2" rx="0.5" fill={upColor} />
            </svg>
          </button>

          {/* Sell Only Mode (Asks) */}
          <button
            type="button"
            onClick={() => setBookViewMode("asks")}
            title="Sell Orders Only"
            style={{
              background: bookViewMode === "asks" ? (theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)") : "transparent",
              border: "none",
              padding: "2px 4px",
              borderRadius: "3px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="2" rx="0.5" fill={downColor} />
              <rect x="2" y="5" width="12" height="2" rx="0.5" fill={downColor} />
              <rect x="2" y="8" width="12" height="2" rx="0.5" fill={downColor} />
              <rect x="2" y="11" width="12" height="2" rx="0.5" fill={downColor} />
            </svg>
          </button>
        </div>
      </div>

      {/* TABLE HEADER */}
      <div className="orderbook_header tour2" style={{ flexShrink: 0 }}>
        <table style={{ width: "100%", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "33%" }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="text-start position-relative"
                style={{
                  color: theme === "light" ? "black" : "",
                  whiteSpace: "nowrap",
                }}
              >
                Amount
                <InfoIcon
                  style={{
                    color: "#a8a1a1",
                    cursor: "pointer",
                    marginLeft: "4px",
                    fontSize: "15px",
                    verticalAlign: "middle",
                  }}
                  onClick={() => setShow(true)}
                />
              </th>
              <th
                className="text-end"
                style={{
                  color: theme === "light" ? "black" : "",
                  whiteSpace: "nowrap",
                }}
              >
                Price
              </th>
              <th
                className="text-end"
                style={{
                  color: theme === "light" ? "black" : "",
                  whiteSpace: "nowrap",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* ASK TABLE */}
      <div
        className="table_content reverse_table"
        ref={tableContentRef}
        onMouseLeave={() => setHoverInfo(null)}
        onScroll={() => setHoverInfo(null)}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: 0,
          display: bookViewMode === "bids" ? "none" : "block",
        }}
      >
        <table style={{ width: "100%", tableLayout: "fixed" }} className="orderbook_table">
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "33%" }} />
          </colgroup>
          <tbody>
            {asksWithDepth.length ? (
              asksWithDepth.map((d, index) => {
                const isHovered = hoverInfo?.type === "ask" && index >= hoverInfo.index;
                return (
                  <tr
                    key={`${d.price}-${d.amount}-ask`}
                    className={`orderbook-depth-ask ${d.newly ? "bg-flash-red" : ""} ${
                      isHovered
                        ? theme === "light"
                          ? "orderbook-row-hovered-light"
                          : "orderbook-row-hovered"
                        : ""
                    }`}
                    style={{ "--depth": `${d.depthPercent}%` }}
                    onMouseEnter={(e) => handleRowMouseEnter("ask", index, e.currentTarget)}
                    onMouseMove={(e) => handleRowMouseMove("ask", index, e.currentTarget)}
                  >
                    <td className="text-start" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {getAmount(d)}
                    </td>
                    <td
                      style={{ color: downColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      className="text-end"
                      onClick={() => handleSellClick(getPrice(d))}
                    >
                      {getPrice(d)}
                    </td>
                    <td className="text-end" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(getAmount(d) * getPrice(d)).toFixed(6)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="text-center">No Data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* HIGH/LOW PRICE */}
      <div
        className={containerClass}
        style={{
          backgroundColor: "var(--body-color-less)",
          height: "32px",
          minHeight: "32px",
          flexShrink: 0,
        }}
      >
        {currencySymbols[basecurrency?.toUpperCase()] && (
          <span style={{ color: textColor }}>
            {currencySymbols[basecurrency.toUpperCase()]}
          </span>
        )}
        <span style={{ color: textColor, paddingRight: "5px" }}>
          {chartData?.ctp}
        </span>
        <span className="font-sm ml-2" id="pricegreen">
          {toggle ? (
            <span style={{ fontSize: "10px", color: upColor }}>
              <ArrowUpwardIcon sx={{ fontSize: "12px" }} /> 24H H: {highPrice}
            </span>
          ) : (
            <span style={{ fontSize: "10px", color: downColor }}>
              <ArrowDownwardIcon sx={{ fontSize: "12px" }} /> 24H L: {lowPrice}
            </span>
          )}
        </span>

        <a
          href={`/orderbook/${currencycode || "BTC"}_${basecurrency || "USDT"}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open Order Book"
          className="orderbook-chevron-btn"
        >
          <ChevronRightIcon sx={{ fontSize: "18px" }} />
        </a>
      </div>

      {/* BID TABLE */}
      <div
        className="table_content tour3"
        onMouseLeave={() => setHoverInfo(null)}
        onScroll={() => setHoverInfo(null)}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: 0,
          display: bookViewMode === "asks" ? "none" : "block",
        }}
      >
        <table style={{ width: "100%", tableLayout: "fixed", marginTop: "4px" }} className="orderbook_table">
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "33%" }} />
          </colgroup>
          <tbody>
            {bidsWithDepth.length ? (
              bidsWithDepth.map((d, index) => {
                const isHovered = hoverInfo?.type === "bid" && index <= hoverInfo.index;
                return (
                  <tr
                    key={`${d.price}-${d.amount}-bid`}
                    className={`orderbook-depth-bid ${d.newly ? "bg-flash-green" : ""} ${
                      isHovered
                        ? theme === "light"
                          ? "orderbook-row-hovered-light"
                          : "orderbook-row-hovered"
                        : ""
                    }`}
                    style={{ "--depth": `${d.depthPercent}%` }}
                    onMouseEnter={(e) => handleRowMouseEnter("bid", index, e.currentTarget)}
                    onMouseMove={(e) => handleRowMouseMove("bid", index, e.currentTarget)}
                  >
                    <td className="text-start" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {getAmount(d)}
                    </td>
                    <td
                      style={{ color: upColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      className="text-end"
                      onClick={() => handleBuyClick(getPrice(d))}
                    >
                      {getPrice(d)}
                    </td>
                    <td className="text-end" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(getAmount(d) * getPrice(d)).toFixed(6)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="text-center">No Data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* BUY / SELL RATIO BAR */}
      <div
        className="orderbook-ratio-bar"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          width: "100%",
          padding: "10px 8px",
          boxSizing: "border-box",
          userSelect: "none",
          flexShrink: 0,
          borderTop: theme === "light" ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0, whiteSpace: "nowrap" }}>
          <span style={{ color: theme === "light" ? "#707a8a" : "#848e9c", fontWeight: 700, fontSize: "11px" }}>B</span>
          <span style={{ color: upColor, fontWeight: 600, fontSize: "11px" }}>{buyPercent}%</span>
        </div>

        <div
          className="orderbook-ratio-track"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            height: "4px",
            margin: "0 8px",
            gap: "2px",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <div
            className="orderbook-ratio-buy"
            style={{
              width: `${buyBarWidth}%`,
              height: "100%",
              backgroundColor: upColor,
              borderRadius: "2px",
              transition: "width 0.3s ease-in-out",
            }}
          />
          <div
            className="orderbook-ratio-sell"
            style={{
              width: `${sellBarWidth}%`,
              height: "100%",
              backgroundColor: downColor,
              borderRadius: "2px",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0, whiteSpace: "nowrap" }}>
          <span style={{ color: downColor, fontWeight: 600, fontSize: "11px" }}>{sellPercent}%</span>
          <span style={{ color: theme === "light" ? "#707a8a" : "#848e9c", fontWeight: 700, fontSize: "11px" }}>S</span>
        </div>
      </div>

      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />

      <OrderBookTooltip
        hoverInfo={hoverInfo}
        baseCurrency={currencycode}
        quoteCurrency={basecurrency}
        theme={theme}
      />
    </div>
  );
};

export default OrderBook;
